import { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { PRINTER_NUMBER_MAX_LENGTH } from '../constants';
import { RootState } from '../redux/reducers/rootReducer';
import {
  getMobilePrinterNumber,
  isMobilePrinterConnected,
  removeMobilePrinterFromStorage,
  saveMobilePrinterToStorage,
} from '../utils/mobilePrinter';
import { getMobilePrinterList, resetMobilePrinter } from '../redux/actions/mobilePrinter.actions';

export const useMobilePrinter = (shouldFetchPrinterList: boolean) => {
  const dispatch = useDispatch();
  const pickerStore = useSelector((state: RootState) => state.pickerStore);
  const mobilePrinterList = useSelector((state: RootState) => state.mobilePrinter.printerList.printers);
  const mobilePrinterListError = useSelector((state: RootState) => state.mobilePrinter.printerList.error);
  const mobilePrinterListLoading = useSelector((state: RootState) => state.mobilePrinter.printerList.isLoading);

  const hasFetchedRef = useRef(false);
  // Local state
  const [manualPrinterNumber, setManualPrinterNumber] = useState('');
  const [selectedPrinterFromList, setSelectedPrinterFromList] = useState('');
  const [isPrinterConnected, setIsPrinterConnected] = useState(isMobilePrinterConnected());
  const [connectedPrinterNumber, setConnectedPrinterNumber] = useState(getMobilePrinterNumber());

  // Fetch printer list
  useEffect(() => {
    if (shouldFetchPrinterList && !hasFetchedRef.current && !isPrinterConnected) {
      dispatch(getMobilePrinterList({ storeId: pickerStore.storeID }));
      hasFetchedRef.current = true;
    }

    return () => {
      hasFetchedRef.current = false;
    };
  }, [isPrinterConnected, dispatch, shouldFetchPrinterList, pickerStore.storeID]);

  const shouldDisableConnectButton = !isPrinterConnected && !manualPrinterNumber && !selectedPrinterFromList;

  // Handle manual printer number input change
  const handleManualPrinterNumberChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value.replace(/[^0-9]/g, '').slice(0, PRINTER_NUMBER_MAX_LENGTH);
    setManualPrinterNumber(inputValue);
    setSelectedPrinterFromList('');
  }, []);

  const handlePrinterSelectionFromList = useCallback((printer: string) => {
    setSelectedPrinterFromList(printer);
    setManualPrinterNumber('');
  }, []);

  const connectPrinter = useCallback(() => {
    const printerToConnect = manualPrinterNumber.trim() || selectedPrinterFromList;
    setIsPrinterConnected(true);
    setConnectedPrinterNumber(printerToConnect);
    saveMobilePrinterToStorage(printerToConnect);
    // TODO: ADD a API call to test printer connection RTRN-5379
  }, [manualPrinterNumber, selectedPrinterFromList]);

  const disconnectPrinter = useCallback(() => {
    setManualPrinterNumber('');
    setSelectedPrinterFromList('');
    setIsPrinterConnected(false);
    setConnectedPrinterNumber('');
    removeMobilePrinterFromStorage();
    dispatch(resetMobilePrinter());
  }, [dispatch]);

  const skipPrinterConnection = useCallback(() => {
    saveMobilePrinterToStorage('');
  }, []);

  const handlePrinterConnectionToggle = useCallback(() => {
    if (isPrinterConnected) {
      disconnectPrinter();
    } else {
      connectPrinter();
    }
  }, [isPrinterConnected, connectPrinter, disconnectPrinter]);

  const resetToLocalStorageState = useCallback(() => {
    const printerNumber = getMobilePrinterNumber();
    const isConnected = isMobilePrinterConnected();
    setConnectedPrinterNumber(printerNumber);
    setIsPrinterConnected(isConnected);
  }, []);

  return {
    shouldDisableConnectButton,
    // State
    manualPrinterNumber,
    mobilePrinterList,
    mobilePrinterListError,
    mobilePrinterListLoading,
    selectedPrinterFromList,
    isPrinterConnected,
    connectedPrinterNumber,

    // Handlers
    handleManualPrinterNumberChange,
    handlePrinterConnectionToggle,
    handlePrinterSelectionFromList,
    connectPrinter,
    disconnectPrinter,
    skipPrinterConnection,
    resetToLocalStorageState,
  };
};
